import type { JobStatus } from "./types";

/**
 * How often a non-terminal job is re-read. Runners write progress in batches,
 * so a tighter interval mostly re-fetches identical rows.
 */
export const POLL_INTERVAL_MS = 5000;

const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  "complete",
  "failed",
  "cancelled",
]);

/** Whether a job has settled and can no longer change. */
export function isTerminal(status: JobStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

function activeDocument(): Document | undefined {
  return typeof document === "undefined" ? undefined : document;
}

function isHidden(): boolean {
  return activeDocument()?.visibilityState === "hidden";
}

/**
 * Repeating timer that only runs while the tab is visible.
 *
 * Starting is idempotent, so a caller can re-assert "should be polling" after
 * every state change without tracking whether the timer already exists.
 */
export class VisibilityPoller {
  private timer: ReturnType<typeof setInterval> | null = null;
  private started = false;

  constructor(
    private readonly onTick: () => void,
    private readonly intervalMs: number = POLL_INTERVAL_MS,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    activeDocument()?.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.resume();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    activeDocument()?.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.pause();
  }

  /** Ticks immediately on re-show so a returning tab is not an interval behind. */
  private readonly handleVisibilityChange = (): void => {
    if (isHidden()) {
      this.pause();
      return;
    }
    this.onTick();
    this.resume();
  };

  private resume(): void {
    if (this.timer !== null || isHidden()) return;
    this.timer = setInterval(this.onTick, this.intervalMs);
  }

  private pause(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
